import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Container,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import html2pdf from 'html2pdf.js';

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

// Services
import * as saleOrderService from "../services/saleOrderService";
import { generateSaleOrderPdfHtml } from "../services/saleOrderPdfService";
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

// Components
import SaleOrderStatusChip from "../components/SaleOrderStatusChip";
import PaymentStatusChip from "../components/PaymentStatusChip";
import RecordPaymentDialog from "../components/RecordPaymentDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';

function SaleOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const reduxLanguage = useSelector((state) => state.settings?.language);
  const language = reduxLanguage || i18n.language || 'en';
  
  // State
  const [saleOrder, setSaleOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfHtml, setPdfHtml] = useState('');

  // Load sale order
  useEffect(() => {
    loadSaleOrder();
    loadPayments();
  }, [id]);

  const loadSaleOrder = async () => {
    setLoading(true);
    try {
      const response = await saleOrderService.getSaleOrderById(id);
      const data = response?.payload || response?.data || response;
      setSaleOrder(data);
    } catch (error) {

      toast.error(t('failed_to_load_sale_order'));
      navigate('/sales/sale-orders');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await saleOrderService.getSaleOrderPayments(id);
      const data = response?.payload || response?.data || response || [];
      setPayments(data);
    } catch (error) {

      // Don't show error toast for payments as it's not critical
    }
  };

  // Handle record payment
  const handleRecordPayment = async (paymentData) => {
    try {
      await saleOrderService.recordSaleOrderPayment(id, paymentData);
      toast.success(t('payment_recorded'));
      loadSaleOrder();
      loadPayments();
    } catch (error) {

      toast.error(error.message || t('failed_to_record_payment'));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    // Prevent deletion of closed sale orders
    if (saleOrder?.Status?.toLowerCase() === 'closed') {
      toast.error(t('cannot_delete_closed_sale_order') || 'Cannot delete a closed sale order. Please reopen it first.');
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      await saleOrderService.deleteSaleOrder(id);
      toast.success(t('sale_order_deleted'));
      navigate('/sales/sale-orders');
    } catch (error) {

      toast.error(error.message || t('failed_to_delete_sale_order'));
    }
  };

  // Handle close sale order
  const handleCloseSaleOrder = async () => {
    if (grandTotal <= 0) {
      toast.error(t('cannot_close_sale_order_zero_total') || 'Cannot close a sale order with zero total amount.');
      setCloseDialogOpen(false);
      return;
    }
    try {
      const closeData = {
        Id: parseInt(id),
        SaleOrderNumber: saleOrder.SaleOrderNumber,
        SaleOrderDate: saleOrder.SaleOrderDate,
        CustomerId: saleOrder.CustomerId || saleOrder.Customer?.Id,
        CustomerName: saleOrder.Customer?.CompanyName || saleOrder.Customer?.Name || saleOrder.CustomerName,
        CustomText: saleOrder.CustomText ?? null,
        VATPercentage: saleOrder.VATPercentage ?? 0,
        DiscountPercentage: saleOrder.DiscountPercentage ?? null,
        DiscountAmount: saleOrder.DiscountAmount ?? null,
        TotalExcl: saleOrder.TotalExcl ?? totalExcl,
        TotalVAT: saleOrder.TotalVAT ?? totalVAT,
        TotalIncl: grandTotal,
        PaidAmount: paidAmount,
        DueAmount: dueAmount,
        PaymentStatus: saleOrder.PaymentStatus || 'Not Paid',
        IsTvaIncluded: saleOrder.IsTvaIncluded ?? false,
        TotalProfit: saleOrder.TotalProfit ?? null,
        Status: 'Closed',
        LineItems: (saleOrder.LineItems || []).map((item) => ({
          Id: item.Id,
          ProductId: item.ProductId ?? null,
          ProductName: item.ProductName || item.Name || item.DisplayName || '',
          Quantity: Number(item.Quantity || 1),
          UnitPrice: Number(item.UnitPrice || item.Price || 0),
          UnitPriceExclTVA: Number(item.UnitPriceExclTVA || 0),
          UnitPriceInclTVA: Number(item.UnitPriceInclTVA || item.UnitPrice || item.Price || 0),
          TotalExcl: Number(item.TotalExcl || 0),
          TotalIncl: Number(item.TotalIncl || 0),
          VATAmount: Number(item.VATAmount || item.TotalVAT || 0),
          CostPrice: Number(item.CostPrice || 0),
        })),
      };
      await saleOrderService.closeSaleOrder(id, closeData);
      toast.success(t('sale_order_closed_successfully'));
      loadSaleOrder();
    } catch (error) {

      toast.error(error.message || t('failed_to_close_sale_order'));
    }
  };

  // Handle reopen sale order
  const handleReopenSaleOrder = () => {
    // Navigate to edit page with reopen flag - this will trigger a GET call and make form editable
    navigate(`/sales/sale-orders/add?id=${id}&reopen=true`);
  };

  // Handle create delivery note from sale order
  const handleCreateDeliveryNote = () => {
    const customer = saleOrder.Customer || {};
    const mappedCustomer = {
      Id: customer.Id || saleOrder.CustomerId,
      Name: customer.Name || customer.CompanyName,
      CompanyName: customer.CompanyName || customer.Name,
      Email: customer.Email,
      PhoneNumber: customer.PhoneNumber || customer.Phone,
      VATNumber: customer.VATNumber || customer.VAT,
      Street: customer.Street,
      City: customer.City,
      PostCode: customer.PostCode,
      Country: customer.Country,
      label: customer.CompanyName || customer.Name,
      value: customer.Id || saleOrder.CustomerId,
    };
    const mappedProducts = (saleOrder.LineItems || []).map((item, index) => ({
      _rowKey: `so_${Date.now()}_${index}`,
      Id: item.ProductId || item.Id,
      Name: item.Name || item.ProductName || item.DisplayName || '',
      Quantity: Number(item.Quantity || 1),
      Price: String(item.UnitPrice || item.Price || 0),
      VAT: Number(saleOrder.VATPercentage || 21),
      TotalExcl: String(item.TotalExcl || 0),
      TotalIncl: String(item.TotalIncl || 0),
      VATAmount: String(item.VATAmount || 0),
      TotalVAT: String(item.VATAmount || 0),
      TotalInclVAT: String(item.TotalIncl || 0),
    }));
    navigate('/sales/delivery-notes/add', {
      state: {
        fromSaleOrder: true,
        saleOrderNumber: saleOrder.SaleOrderNumber,
        customer: mappedCustomer,
        lineItems: mappedProducts,
        vatPercentage: Number(saleOrder.VATPercentage || 21),
      },
    });
  };

  // Handle preview PDF
  const handlePreviewPdf = async () => {
    try {
      const saleOrderData = {
        SaleOrderNumber: saleOrder.SaleOrderNumber,
        SaleOrderDate: saleOrder.SaleOrderDate || saleOrder.CreatedDate,
        Customer: saleOrder.Customer,
        LineItems: saleOrder.LineItems,
        CustomText: saleOrder.CustomText,
        VATPercentage: saleOrder.VATPercentage,
        Status: saleOrder.Status,
        PaymentStatus: saleOrder.PaymentStatus,
        PaidAmount: saleOrder.PaidAmount || 0,
        TotalIncl: saleOrder.TotalIncl || grandTotal,
        DiscountAmount: saleOrder.DiscountAmount || 0,
        IsTvaIncluded: saleOrder.IsTvaIncluded,
      };
      const htmlContent = generateSaleOrderPdfHtml(saleOrderData, storeInfo, language);
      setPdfHtml(htmlContent);
      setPdfPreviewOpen(true);
    } catch (error) {

      toast.error(t('failed_to_generate_pdf'));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!saleOrder) {
    return null;
  }

  const customer = saleOrder.Customer || {};
  const lineItems = saleOrder.LineItems || [];
  const totalExcl = Number(saleOrder.TotalExcl || lineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0));
  const totalVAT = Number(saleOrder.TotalVAT || lineItems.reduce((sum, item) => sum + Number(item.VATAmount || 0), 0));
  // Use TotalIncl from sale order (which includes discount) instead of summing line items
  const grandTotal = Number(saleOrder.TotalIncl || lineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0));
  const paidAmount = Number(saleOrder.PaidAmount || 0);
  const dueAmount = grandTotal - paidAmount;
  
  // Calculate discount for display
  const hasDiscount = (saleOrder.DiscountPercentage > 0) || (saleOrder.DiscountAmount > 0);
  const discountAmount = Number(saleOrder.DiscountAmount || 0);
  const discountPercentage = Number(saleOrder.DiscountPercentage || 0);
  // Subtotal before discount
  const lineItemsTotal = lineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
  
  const isDraft = saleOrder.Status?.toLowerCase() === 'draft';
  const isClosed = saleOrder.Status?.toLowerCase() === 'closed';
  const canRecordPayment = isClosed && dueAmount > 0;
  const isAlreadyConverted = Boolean(saleOrder.InvoiceId || saleOrder.InvoiceNumber);
  const canConvertToInvoice = isClosed && !isAlreadyConverted;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {saleOrder.SaleOrderNumber}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <SaleOrderStatusChip status={saleOrder.Status} sx={{ bgcolor: 'white', color: '#667eea' }} />
                <PaymentStatusChip paymentStatus={saleOrder.PaymentStatus} sx={{ bgcolor: 'white' }} />
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/sales/sale-orders')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              fontWeight: 700,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            {t('back')}
          </Button>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isDraft && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales/sale-orders/add?id=${id}`)}
              >
                {t('continue')}
              </Button>
              <Tooltip title={grandTotal <= 0 ? (t('add_products_to_close') || 'Add products before closing') : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => setCloseDialogOpen(true)}
                    disabled={grandTotal <= 0}
                    sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
                  >
                    {t('close_sale_order')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {isClosed && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleReopenSaleOrder}
              sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' } }}
            >
              {t('reopen_sale_order')}
            </Button>
          )}
          {canRecordPayment && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialogOpen(true)}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            >
              {t('record_payment')}
            </Button>
          )}
          {canConvertToInvoice && (
            <Button
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => navigate(`/sales/sale-orders/convert?id=${id}`)}
              sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {t('convert_to_invoice')}
            </Button>
          )}
          {isAlreadyConverted && (
            <Button
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => navigate(`/invoices/view/${saleOrder.InvoiceId}`)}
              sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
            >
              {t('view_invoice')}: {saleOrder.InvoiceNumber}
            </Button>
          )}
          {isClosed && (
            <Button
              variant="contained"
              startIcon={<LocalShippingIcon />}
              onClick={handleCreateDeliveryNote}
              sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}
            >
              {t('create_delivery_note') || 'Create Delivery Note'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePreviewPdf}
          >
            {t('preview_pdf')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isClosed}
          >
            {t('delete')}
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Customer Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('customer_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('company_name')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {customer.CompanyName || customer.Name || t('unknown')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('email')}
                      </Typography>
                      <Typography variant="body2">
                        {customer.Email || t('no_email')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('phone')}
                      </Typography>
                      <Typography variant="body2">
                        {customer.PhoneNumber || customer.Phone || t('no_phone')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('address')}
                      </Typography>
                      <Typography variant="body2">
                        {[customer.Street, customer.City, customer.PostCode, customer.Country]
                          .filter(Boolean)
                          .join(', ') || t('no_address')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('line_items')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('product')}</TableCell>
                      <TableCell align="center">{t('sku_barcode') || 'SKU/BarCode'}</TableCell>
                      <TableCell align="center">{t('quantity')}</TableCell>
                      <TableCell align="right">{t('unit_price')}</TableCell>
                      <TableCell align="right">{t('total_excl_vat')}</TableCell>
                      <TableCell align="right">{t('vat')}</TableCell>
                      <TableCell align="right">{t('total_incl_vat')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.Name || item.ProductName}</TableCell>
                        <TableCell align="center">{item.SKUBarCode || item.SKU || item.BarCode || '-'}</TableCell>
                        <TableCell align="center">{item.Quantity}</TableCell>
                        <TableCell align="right">€{Number(item.UnitPrice || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">€{Number(item.TotalExcl || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">€{Number(item.VATAmount || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          €{Number(item.TotalIncl || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {/* Payment History */}
          {payments.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('payment_history')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('date')}</TableCell>
                        <TableCell>{t('payment_method')}</TableCell>
                        <TableCell align="right">{t('amount')}</TableCell>
                        <TableCell>{t('notes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>{formatDate(payment.PaymentDate)}</TableCell>
                          <TableCell>{payment.PaymentMethodName || t('unknown')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            €{Number(payment.Amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{payment.Notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('summary')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('date')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(saleOrder.SaleOrderDate || saleOrder.CreatedDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('items')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {lineItems.length}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('subtotal')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    €{lineItemsTotal.toFixed(2)}
                  </Typography>
                </Box>
                
                {hasDiscount && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="warning.main">
                      {t('discount')} {discountPercentage > 0 ? `(${discountPercentage}%)` : ''}:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      -€{discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('vat')} ({saleOrder.VATPercentage || 21}%):
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    €{totalVAT.toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={700}>
                    {t('total')}:
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    €{grandTotal.toFixed(2)}
                  </Typography>
                </Box>
                
                {paidAmount > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="success.main">
                        {t('paid')}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        €{paidAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="error.main">
                        {t('due')}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="error.main">
                        €{dueAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {saleOrder.CustomText && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {t('notes')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {saleOrder.CustomText}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <RecordPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onConfirm={handleRecordPayment}
        saleOrder={saleOrder}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete')}
        message={t('confirm_delete_sale_order_message', { number: saleOrder.SaleOrderNumber })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />

      <ConfirmationDialog
        open={closeDialogOpen}
        title={t('close_sale_order')}
        message={t('confirm_close_sale_order_message')}
        onConfirm={handleCloseSaleOrder}
        onCancel={() => setCloseDialogOpen(false)}
        confirmText={t('close')}
        cancelText={t('cancel')}
        variant="success"
      />

      <InvoicePreviewDialog
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        html={pdfHtml}
        documentTitle={`${t('sale_order')} ${saleOrder?.SaleOrderNumber || ''}`}
      />
    </Container>
  );
}

export default SaleOrderDetailPage;
